import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Save, ChevronRight, Eye, EyeOff, GripVertical,
  Layout, Layers, FolderOpen, Server, Info, MessageSquare, Navigation, Globe,
  Check, AlertCircle,
} from "lucide-react";
import MayrLogo from "@/components/MayrLogo";
import HeroEditor from "@/admin/HeroEditor";
import ServicesEditor from "@/admin/ServicesEditor";
import ProjectsEditor from "@/admin/ProjectsEditor";
import CapabilitiesEditor from "@/admin/CapabilitiesEditor";
import AboutEditor from "@/admin/AboutEditor";
import CtaEditor from "@/admin/CtaEditor";
import NavbarEditor from "@/admin/NavbarEditor";
import FooterEditor from "@/admin/FooterEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = {
  hero: { label: "Hero", icon: Layout },
  services: { label: "Services", icon: Layers },
  projects: { label: "Projects", icon: FolderOpen },
  capabilities: { label: "Infrastructure", icon: Server },
  about: { label: "About", icon: Info },
  cta: { label: "CTA", icon: MessageSquare },
  navbar: { label: "Navbar", icon: Navigation },
  footer: { label: "Footer", icon: Globe },
};

const SECTION_ORDER = ["hero", "navbar", "services", "projects", "capabilities", "about", "cta", "footer"];

const EDITORS = {
  hero: HeroEditor,
  services: ServicesEditor,
  projects: ProjectsEditor,
  capabilities: CapabilitiesEditor,
  about: AboutEditor,
  cta: CtaEditor,
  navbar: NavbarEditor,
  footer: FooterEditor,
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [activeSection, setActiveSection] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const token = localStorage.getItem("admin_token");

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    axios.get(`${BACKEND_URL}/api/admin/me`, authHeaders())
      .catch(() => { localStorage.removeItem("admin_token"); navigate("/admin"); });
  }, [token, navigate, authHeaders]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/content`)
      .then(({ data }) => setContent(data))
      .catch(console.error);
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (section, data) => {
    setSaving(true);
    try {
      const { data: updated } = await axios.put(
        `${BACKEND_URL}/api/content/${section}`,
        data,
        authHeaders()
      );
      setContent((prev) => ({ ...prev, [section]: updated }));
      showToast(`${SECTION_META[section]?.label || section} saved`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (section) => {
    const current = content[section];
    if (!current) return;
    const updated = { ...current, visible: !current.visible };
    await handleSave(section, updated);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin");
  };

  const ActiveEditor = EDITORS[activeSection];
  const sectionData = content[activeSection];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#030303] flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`${sidebarOpen ? "w-64" : "w-0 overflow-hidden"} flex-shrink-0 border-r border-white/[0.06] bg-[#050505] transition-all duration-300 flex flex-col`}
      >
        <div className="p-5 border-b border-white/[0.06]">
          <MayrLogo height={24} />
          <p className="text-[10px] font-medium text-neutral-600 font-['Manrope'] mt-2 uppercase tracking-[0.2em]">
            Content Manager
          </p>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {SECTION_ORDER.map((key) => {
            const meta = SECTION_META[key];
            const Icon = meta.icon;
            const isActive = activeSection === key;
            const isVisible = content[key]?.visible !== false;
            return (
              <button
                key={key}
                data-testid={`sidebar-section-${key}`}
                onClick={() => setActiveSection(key)}
                className={`w-full flex items-center gap-3 px-5 py-2.5 text-left text-sm font-['Manrope'] font-medium transition-colors duration-200
                  ${isActive
                    ? "bg-white/[0.06] text-white border-r-2 border-white"
                    : "text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.02]"
                  }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1 truncate">{meta.label}</span>
                {!isVisible && <EyeOff className="w-3 h-3 text-neutral-700" />}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/[0.06] space-y-2">
          <button
            data-testid="admin-view-site"
            onClick={() => window.open("/", "_blank")}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-neutral-500 font-['Manrope'] hover:text-neutral-300 hover:bg-white/[0.03] transition-colors duration-200"
          >
            <Eye className="w-3.5 h-3.5" /> View Site
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-neutral-500 font-['Manrope'] hover:text-red-400 hover:bg-red-500/[0.05] transition-colors duration-200"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-14 border-b border-white/[0.06] flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              data-testid="toggle-sidebar"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-white/[0.05] transition-colors duration-200"
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <span className="text-xs text-neutral-600 font-['Manrope']">
              Editing
            </span>
            <ChevronRight className="w-3 h-3 text-neutral-700" />
            <span className="text-sm text-white font-['Manrope'] font-medium">
              {SECTION_META[activeSection]?.label}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid={`toggle-visibility-${activeSection}`}
              onClick={() => toggleVisibility(activeSection)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-['Manrope'] font-medium transition-colors duration-200
                ${content[activeSection]?.visible !== false
                  ? "text-green-400 bg-green-500/[0.08] hover:bg-green-500/[0.12]"
                  : "text-neutral-500 bg-white/[0.04] hover:bg-white/[0.06]"
                }`}
            >
              {content[activeSection]?.visible !== false ? (
                <><Eye className="w-3.5 h-3.5" /> Visible</>
              ) : (
                <><EyeOff className="w-3.5 h-3.5" /> Hidden</>
              )}
            </button>
          </div>
        </header>

        {/* Editor Area */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-3xl">
            {sectionData && ActiveEditor ? (
              <ActiveEditor
                data={sectionData}
                onSave={(data) => handleSave(activeSection, data)}
                saving={saving}
              />
            ) : (
              <div className="text-neutral-500 text-sm font-['Manrope']">Loading section data...</div>
            )}
          </div>
        </main>
      </div>

      {/* Toast */}
      {toast && (
        <div
          data-testid="save-toast"
          className={`fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-['Manrope'] font-medium shadow-lg z-50 transition-all duration-300
            ${toast.type === "error"
              ? "bg-red-500/20 border border-red-500/30 text-red-300"
              : "bg-green-500/20 border border-green-500/30 text-green-300"
            }`}
        >
          {toast.type === "error" ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
